import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Inbox, Layers, Lock, MapPin, Plus } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ScopeExtensionRequestModal } from "@/features/rfq/components/ScopeExtensionRequestModal";
import { CountyDisclosure } from "@/features/rfq/components/CountyRefinement";
import { EXTENSION_STATUS_COLOR, useScopeRequests } from "@/features/rfq/api/requests";

/**
 * „Mój zakres współpracy" — widok podwykonawcy.
 *
 * Pokazujemy CAŁY katalog kategorii, nie tylko przyznane: bez tego firma nie wie,
 * o co jeszcze może wystąpić. Obce kategorie to jednak SAME NAZWY — rozpiska pozycji
 * mówi, jak dzielimy pracę u siebie, więc API nie zwraca jej dla nieprzyznanych.
 */

interface Position {
  id: string;
  name: string;
  description: string;
  granted: boolean;
}

interface Category {
  id: string;
  name: string;
  description: string;
  granted: boolean;
  status: "full" | "conditional" | null;
  status_label: string | null;
  all_positions: boolean;
  granted_at: string | null;
  positions: Position[];
}

interface MyScope {
  categories: Category[];
  voivodeships: {
    value: string;
    label: string;
    granted: boolean;
    /** Zawężenie do powiatów; pusta lista = całe województwo. */
    counties: { teryt: string; name: string }[];
    county_total: number;
  }[];
}

export function MyScopePage() {
  usePageTitle("Mój zakres współpracy");
  const [requesting, setRequesting] = useState(false);
  const { data: myRequests } = useScopeRequests();

  const { data, isLoading } = useQuery({
    queryKey: ["rfq", "my-scope"],
    queryFn: () =>
      apiClient
        .get<{ data: MyScope }>("/rfq/my-scope/")
        .then((r) => r.data.data),
  });

  if (isLoading || !data) return <PageLoader />;

  const granted = data.categories.filter((c) => c.granted);
  const available = data.categories.filter((c) => !c.granted);
  const areas = data.voivodeships.filter((v) => v.granted);

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Mój zakres współpracy
        </h1>
        <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
        <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          Zakres decyduje o tym, do jakich zapytań ofertowych mają Państwo
          dostęp - system bierze pod uwagę kategorie prac razem z obszarem
          działania. Uprawnienia nadaje GK ELEKTIM.
        </p>
        </div>
        <Button onClick={() => setRequesting(true)}>
          <Plus size={14} className="mr-1.5 inline" /> Wnioskuj o rozszerzenie
        </Button>
      </div>

      {/* Obszar działania */}
      <section className="mb-8">
        <h2 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          <MapPin size={12} /> Obszar działania
        </h2>
        {areas.length ? (
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {areas.map((v) => (
              <span key={v.value} className="inline-flex items-center gap-1.5">
                <Badge color="blue">{v.label}</Badge>
                <CountyDisclosure
                  names={v.counties.map((c) => c.name)}
                  total={v.county_total}
                />
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            Nie wskazano żadnego województwa.
          </p>
        )}
      </section>

      {/* Przyznane kategorie */}
      <section className="mb-8">
        <h2 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          <Layers size={12} /> Przyznane kategorie ({granted.length})
        </h2>
        {granted.length === 0 ? (
          <div className="border border-dashed border-[var(--color-border)] px-6 py-8 text-center text-sm text-[var(--color-text-secondary)]">
            Nie mają Państwo jeszcze przyznanej żadnej kategorii prac.
          </div>
        ) : (
          <div className="space-y-3">
            {granted.map((category) => (
              <article
                key={category.id}
                className="border-l-4 border-[var(--color-primary)] bg-white p-4 dark:bg-gray-800"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-[var(--color-text-primary)]">
                    {category.name}
                  </h3>
                  <Badge
                    color={
                      category.status === "conditional" ? "yellow" : "green"
                    }
                  >
                    {category.status_label}
                  </Badge>
                  {!category.all_positions && (
                    <span className="text-xs text-[var(--color-text-muted)]">
                      zakres ograniczony
                    </span>
                  )}
                </div>
                {category.description && (
                  <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                    {category.description}
                  </p>
                )}
                {category.positions.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {category.positions.map((position) => (
                      <li
                        key={position.id}
                        className={`flex items-start gap-2 text-sm ${
                          position.granted
                            ? "text-[var(--color-text-primary)]"
                            : "text-[var(--color-text-muted)] line-through"
                        }`}
                      >
                        {position.granted ? (
                          <Check
                            size={13}
                            className="mt-1 shrink-0 text-green-600 dark:text-green-400"
                          />
                        ) : (
                          <Lock size={12} className="mt-1 shrink-0" />
                        )}
                        {position.name}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Pozostałe kategorie */}
      <section>
        <h2 className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          <Lock size={12} /> Pozostałe kategorie ({available.length})
        </h2>
        <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
          Jeśli wykonują Państwo takie prace, proszę złożyć wniosek o rozszerzenie
          zakresu — rozpatruje go dyrektor techniczny GK ELEKTIM.
        </p>
        {available.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Mają Państwo dostęp do wszystkich kategorii.
          </p>
        ) : (
          <div className="grid gap-px bg-[var(--color-border)] sm:grid-cols-2">
            {available.map((category) => (
              <div key={category.id} className="bg-white p-3 dark:bg-gray-800">
                {/* Sama nazwa — rozpiskę pozycji API oddaje dopiero przy przyznanej kategorii. */}
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {category.name}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Moje wnioski */}
      {myRequests && myRequests.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            <Inbox size={12} /> Moje wnioski ({myRequests.length})
          </h2>
          <div className="space-y-2">
            {myRequests.map((request) => (
              <article key={request.id} className="bg-white p-4 dark:bg-gray-800">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge color={EXTENSION_STATUS_COLOR[request.status]}>
                    {request.status_label}
                  </Badge>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    złożony {new Date(request.created_at).toLocaleDateString("pl")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                  {[
                    ...request.categories.map((c) => c.name),
                    ...request.positions.map((p) => `${p.category_name}: ${p.name}`),
                    ...request.voivodeship_labels,
                  ].join(" · ")}
                </p>
                {request.decision_note && (
                  <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                    Odpowiedź GK ELEKTIM: {request.decision_note}
                  </p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <ScopeExtensionRequestModal
        open={requesting}
        onClose={() => setRequesting(false)}
        categories={data.categories}
        voivodeships={data.voivodeships}
      />
    </div>
  );
}
